import { get } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { serializeAppliedFilters } from 'donor-elf-web/lib/filter-utils-new';
import FundModel from 'donor-elf-web/models/fund';
import Highcharts from 'highcharts';
import moment from 'moment';

/*
  attrs
  - goal
  - fund
  - excludePledges (boolean) default false
*/

interface DashboardRollingGoalComponentArgs {
  goal: any;
  fund: FundModel;
  excludePledges: boolean;
  disclaimerLabel?: string; // optional label for the disclaimer
  disclaimerLink?: string; // optional link for the disclaimer
}

export default class DashboardRollingGoalComponent extends Component<DashboardRollingGoalComponentArgs> {
  @service declare formatAmountService: any;
  @service declare media: any;
  @service declare settings: any;
  @service declare router: RouterService;

  @tracked renderMonthlyGiftsChart = false;

  fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  get goal() {
    return this.args.goal;
  }

  get excludePledges() {
    return this.args.excludePledges ?? false;
  }

  get isAnnualTimeFrame() {
    return this.settings.userSettings.showAnnualGoal;
  }

  get timeFrameAdjective() {
    return this.isAnnualTimeFrame ? 'Annual' : 'Monthly';
  }

  timeFrameAmount(amount: number) {
    return this.isAnnualTimeFrame ? amount * 12 : amount;
  }

  get goalAmount() {
    return this.timeFrameAmount(this.goal.goalAmount);
  }

  get lookingBackAmount() {
    return this.isAnnualTimeFrame
      ? this.goal.goalData.last_year_income
      : this.goal.goalData.last_year_avg_income;
  }

  get lookingBackChartSettings() {
    let percentage = this.goal.goalData.last_year_avg_percentage;
    if (percentage > 100) {
      percentage = 100;
    }
    let fillerPercentage = 100 - percentage;
    let formattedAmount = this.formatAmountService.format(
      this.lookingBackAmount,
      { roundToDollar: true },
    );

    return {
      chart: {
        type: 'pie',
        spacing: [0, 0, 0, 0],
      },
      title: {
        text: `<span>${this.goal.goalData.last_year_avg_percentage}%</span><div style='font-size: 1rem; color: #636c72'>${formattedAmount}</div>`,
        align: 'center',
        verticalAlign: 'middle',
        useHTML: true,
        style: {
          fontSize: '2rem',
          color: '#0b68a1',
          fontFamily: this.fontFamily,
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          shadow: false,
          center: ['50%', '50%'],
        },
      },
      series: [
        {
          size: '100%',
          innerSize: '90%',
          dataLabels: {
            enabled: false,
          },
          data: [
            {
              y: percentage,
              color: '#0b68a1',
            },
            {
              y: fillerPercentage,
              color: '#eceeef',
            },
          ],
        },
      ],
    } as Highcharts.Options;
  }

  get fundedChartSettings() {
    let percentage = this.goal.goalData.pledged_monthly_percentage;
    if (percentage > 100) {
      percentage = 100;
    }
    let fillerPercentage = 100 - percentage;
    let formattedAmount = this.formatAmountService.format(
      this.timeFrameAmount(this.goal.goalData.pledged_monthly_amount),
      { roundToDollar: true },
    );

    return {
      chart: {
        type: 'pie',
        spacing: [0, 0, 0, 0],
      },
      title: {
        text: `<span>${this.goal.goalData.pledged_monthly_percentage}%</span><div style='font-size: 1rem; color: #636c72'>${formattedAmount}</div>`,
        align: 'center',
        verticalAlign: 'middle',
        useHTML: true,
        style: {
          fontSize: '2rem',
          color: '#f0ad4e',
          fontFamily: get(this, 'fontFamily'),
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          shadow: false,
          center: ['50%', '50%'],
        },
      },
      series: [
        {
          size: '100%',
          innerSize: '90%',
          dataLabels: {
            enabled: false,
          },
          data: [
            {
              name: 'Looking Forward',
              y: percentage,
              color: '#f0ad4e',
            },
            {
              y: fillerPercentage,
              color: '#eceeef',
            },
          ],
        },
      ],
    } as Highcharts.Options;
  }

  templateDidInsert = (el: any) => {
    if (this.media.isMediumDown) {
      this.renderMonthlyGiftsChart = true;
      Highcharts.chart(
        el.querySelector('.looking-back-chart'),
        this.lookingBackChartSettings,
      );
      if (!this.excludePledges) {
        Highcharts.chart(
          el.querySelector('.looking-forward-chart'),
          this.fundedChartSettings,
        );
      }
    } else {
      setTimeout(() => {
        if (!this.isDestroyed) {
          Highcharts.chart(
            el.querySelector('.looking-back-chart'),
            this.lookingBackChartSettings,
          );
        }
      }, 300);
      if (!this.excludePledges) {
        setTimeout(() => {
          if (!this.isDestroyed) {
            Highcharts.chart(
              el.querySelector('.looking-forward-chart'),
              this.fundedChartSettings,
            );
          }
        }, 900);
      }
      setTimeout(() => {
        if (!this.isDestroyed) {
          this.renderMonthlyGiftsChart = true;
        }
      }, 1400);
    }
  };

  handlePastYearClicked = () => {
    this.router.transitionTo('funds.fund.transactions', {
      queryParams: {
        filters: serializeAppliedFilters([
          {
            propertyName: 'transDate',
            operation: '><',
            val: [
              moment(this.goal.goalData.last_year_from).format('YYYY-MM-DD'),
              moment(this.goal.goalData.last_year_to).format('YYYY-MM-DD'),
            ],
          },
          { propertyName: 'income' },
          { propertyName: 'affectsGoal' },
        ]),
      },
    });
  };

  handleLookingForwardClicked = () => {
    this.router.transitionTo('funds.fund.pledges', {
      queryParams: {
        filters: serializeAppliedFilters([
          {
            propertyName: 'startDate',
            operation: '>=',
            val: moment(this.goal.goalData.pledged_monthly_from).format(
              'YYYY-MM-DD',
            ),
          },
          {
            propertyName: 'endDate',
            operation: '<=',
            val: moment(this.goal.goalData.pledged_monthly_to).format(
              'YYYY-MM-DD',
            ),
          },
          { propertyName: 'affectsGoal', operation: '=', val: true },
        ]),
      },
    });
  };
}
